import { useEffect, useRef, useState } from 'react';
import { Check, Copy } from 'lucide-react';
import toast from 'react-hot-toast';
import { QrFormatPicker } from './QrFormatPicker';
import { copyTextToClipboard } from '../utils/clipboard';
import {
  QR_IMAGE_FORMAT_OPTIONS,
  QrDownloadFormat,
  QrImageFormat,
  qrDownloadFileName,
  qrImageMimeType,
  triggerFileDownload,
} from '../utils/qrDownload';
import { QrShareSelection, buildShareMessage, hasShareSelection, shareEventKit } from '../utils/qrShare';

interface QrShareFlowProps {
  eventName: string;
  eventDateTime: string;
  eventLink: string;
  eventUrl: string;
  createPngBlob: () => Promise<Blob>;
  createSvgBlob: () => Blob;
  onClose: () => void;
}

type ShareStep = 'choose' | 'format' | 'result';

interface ShareFallbackResult {
  message: string;
  copied: boolean;
  downloadedFileName: string | null;
}

const OVERLAY_CLASSES = 'fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4';
const CARD_CLASSES =
  'w-full max-w-xs bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg p-4';
const CHECKBOX_CLASSES = 'mt-0.5 w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary';

function downloadQrFile(file: File): string | null {
  try {
    triggerFileDownload(file, file.name);
    return file.name;
  } catch (error) {
    console.error('[QrShareFlow] Could not download the QR file:', error);
    toast.error('Could not save the QR file. Use Download in the QR dialog instead.');
    return null;
  }
}

export function QrShareFlow({
  eventName,
  eventDateTime,
  eventLink,
  eventUrl,
  createPngBlob,
  createSvgBlob,
  onClose,
}: QrShareFlowProps) {
  const [selection, setSelection] = useState<QrShareSelection>({ qrCode: true, link: true });
  const [step, setStep] = useState<ShareStep>('choose');
  const [files, setFiles] = useState<Record<QrImageFormat, File | null>>({ png: null, svg: null });
  const [filesReady, setFilesReady] = useState(false);
  const [result, setResult] = useState<ShareFallbackResult | null>(null);
  const fileSourcesRef = useRef({ createPngBlob, createSvgBlob, eventUrl });

  useEffect(() => {
    let isMounted = true;
    const sources = fileSourcesRef.current;
    const toFile = (blob: Blob, format: QrImageFormat): File =>
      new File([blob], qrDownloadFileName(sources.eventUrl, format), { type: qrImageMimeType(format) });

    const prepareFiles = async () => {
      let pngFile: File | null = null;
      let svgFile: File | null = null;
      try {
        pngFile = toFile(await sources.createPngBlob(), 'png');
      } catch (error) {
        console.error('[QrShareFlow] Could not prepare the QR file:', error);
      }
      try {
        svgFile = toFile(sources.createSvgBlob(), 'svg');
      } catch (error) {
        console.error('[QrShareFlow] Could not prepare the QR file:', error);
      }
      if (!isMounted) return;
      setFiles({ png: pngFile, svg: svgFile });
      setFilesReady(true);
    };

    prepareFiles();
    return () => {
      isMounted = false;
    };
  }, []);

  const runShare = async (file: File | null) => {
    const message = buildShareMessage({ eventName, eventDateTime, eventLink, selection });
    const outcome = await shareEventKit({ title: eventName, text: message, file });
    if (outcome === 'shared') {
      toast.success('Shared');
      onClose();
      return;
    }
    if (outcome === 'cancelled') return;

    const copied = await copyTextToClipboard(message);
    const downloadedFileName = file ? downloadQrFile(file) : null;
    setResult({ message, copied, downloadedFileName });
    setStep('result');
  };

  const handleContinue = () => {
    if (selection.qrCode) {
      setStep('format');
      return;
    }
    runShare(null);
  };

  const handlePickFormat = (format: QrDownloadFormat) => {
    if (format === 'pdf') return;
    runShare(files[format]);
  };

  const handleCopyMessage = async () => {
    if (!result) return;
    const copied = await copyTextToClipboard(result.message);
    setResult({ ...result, copied });
    if (copied) toast.success('Message copied');
    else toast.error('Could not copy. Select the text and copy it by hand.');
  };

  if (step === 'format') {
    const disabledFormats = QR_IMAGE_FORMAT_OPTIONS
      .map(option => option.format)
      .filter(format => format !== 'pdf' && files[format] === null);
    return (
      <QrFormatPicker
        title="Share QR code as"
        options={QR_IMAGE_FORMAT_OPTIONS}
        disabledFormats={disabledFormats}
        onPick={handlePickFormat}
        onCancel={onClose}
      />
    );
  }

  if (step === 'result' && result) {
    return (
      <div className={OVERLAY_CLASSES}>
        <div data-testid="qr-share-flow" className={CARD_CLASSES}>
          <p className="font-semibold text-gray-900 dark:text-white mb-2">Ready to paste</p>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
            This device couldn't open a share menu here, so we got everything ready for you.
          </p>
          {result.copied ? (
            <p className="text-sm text-gray-700 dark:text-gray-300 flex items-start gap-2 mb-2">
              <Check className="w-4 h-4 mt-0.5 flex-shrink-0 text-green-500" />
              <span>Message copied. Paste it into a text or email.</span>
            </p>
          ) : (
            <p role="alert" className="text-sm text-amber-600 dark:text-amber-400 mb-2">
              The message couldn't be copied automatically. Use Copy message below.
            </p>
          )}
          {result.downloadedFileName && (
            <p className="text-sm text-gray-700 dark:text-gray-300 break-words mb-2">
              QR code saved to your downloads as {result.downloadedFileName}. Attach it to your message.
            </p>
          )}
          <textarea
            className="input min-h-[140px] resize-none text-sm"
            readOnly
            value={result.message}
            aria-label="Message to share"
          />
          <div className="flex gap-3 mt-3">
            <button
              type="button"
              onClick={handleCopyMessage}
              className="btn btn-secondary flex-1 flex items-center justify-center gap-2"
            >
              <Copy className="w-4 h-4" />
              Copy message
            </button>
            <button type="button" onClick={onClose} className="btn btn-primary flex-1">
              Done
            </button>
          </div>
        </div>
      </div>
    );
  }

  const isContinueDisabled = !hasShareSelection(selection) || (selection.qrCode && !filesReady);

  return (
    <div className={OVERLAY_CLASSES}>
      <div data-testid="qr-share-flow" className={CARD_CLASSES}>
        <p className="font-semibold text-gray-900 dark:text-white mb-3">What do you want to share?</p>
        <div className="space-y-2">
          <label className="flex items-start gap-3 p-3 rounded-lg border-2 border-gray-200 dark:border-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={selection.qrCode}
              onChange={event => setSelection({ ...selection, qrCode: event.target.checked })}
              className={CHECKBOX_CLASSES}
            />
            <span className="min-w-0">
              <span className="block text-sm font-medium text-gray-900 dark:text-white">QR code</span>
              <span className="block text-xs text-gray-500 dark:text-gray-400">
                An image to drop into flyers and posts
              </span>
            </span>
          </label>
          <label className="flex items-start gap-3 p-3 rounded-lg border-2 border-gray-200 dark:border-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={selection.link}
              onChange={event => setSelection({ ...selection, link: event.target.checked })}
              className={CHECKBOX_CLASSES}
            />
            <span className="min-w-0">
              <span className="block text-sm font-medium text-gray-900 dark:text-white">Event link</span>
              <span className="block text-xs text-gray-500 dark:text-gray-400 break-all">{eventLink}</span>
            </span>
          </label>
        </div>
        <div className="flex gap-3 mt-4">
          <button type="button" onClick={onClose} className="btn btn-secondary flex-1">
            Cancel
          </button>
          <button type="button" onClick={handleContinue} disabled={isContinueDisabled} className="btn btn-primary flex-1">
            Continue
          </button>
        </div>
      </div>
    </div>
  );
}
